import axios from "axios";

//const API_BASE_URL = "https://localhost:7226/api";
//const API_BASE_URL = "https://milkbilling-api.deltalogapp.online/api";
//const API_BASE_URL = "https://milkbilling.deltalogsystems.com/api";
const API_BASE_URL = "https://milkbilling.deltalogsystems.com/api";
// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor to add token
api.interceptors.request.use(
  (config) => {
    const user = JSON.parse(localStorage.getItem("milkBillingUser"));
    if (user && user.token) {
      config.headers.Authorization = `Bearer ${user.token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor to handle token expiration
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      localStorage.removeItem("milkBillingUser");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  },
);

// Get userId from localStorage
const getUserId = () => {
  const userData = localStorage.getItem("milkBillingUser");
  return userData ? JSON.parse(userData).userId : null;
};

// Get token from localStorage
const getToken = () => {
  const userData = localStorage.getItem("milkBillingUser");
  return userData ? JSON.parse(userData).token : null;
};

// Auth API
export const authAPI = {
  login: async (username, password) => {
    const response = await api.post("/auth/login", {
      userName: username,
      password: password,
    });

    if (response.data.success && response.data.data) {
      localStorage.setItem(
        "milkBillingUser",
        JSON.stringify(response.data.data),
      );
    }

    return response.data;
  },

  register: async (username, email, password, confirmPassword, mobileNo) => {
    const response = await api.post("/auth/register", {
      userName: username,
      email: email,
      password: password,
      confirmPassword: confirmPassword,
      mobileNo: mobileNo,
    });

    if (response.data.success && response.data.data) {
      localStorage.setItem(
        "milkBillingUser",
        JSON.stringify(response.data.data),
      );
    }

    return response.data;
  },

  updateProfile: async (userName, email, mobileNo) => {
    const response = await api.put("/auth/profile", {
      userName: userName,
      email: email,
      mobileNo: mobileNo,
    });
    return response.data;
  },

  logout: async () => {
    try {
      await api.post("/auth/logout");
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      localStorage.removeItem("milkBillingUser");
    }
  },

  validateToken: async () => {
    const response = await api.get("/auth/validate");
    return response.data;
  },

  getCurrentUser: () => {
    return JSON.parse(localStorage.getItem("milkBillingUser"));
  },

  isAuthenticated: () => {
    const user = JSON.parse(localStorage.getItem("milkBillingUser"));
    if (!user || !user.token) return false;

    // Check if token is expired
    if (user.tokenExpiry) {
      const expiry = new Date(user.tokenExpiry);
      if (expiry < new Date()) {
        localStorage.removeItem("milkBillingUser");
        return false;
      }
    }

    return true;
  },
};

// Customer API
// Customer API
export const customerAPI = {
  getCustomers: async () => {
    const userId = getUserId();
    const response = await api.get(`/Customer/${userId}`);
    return response.data;
  },

  addCustomer: async (customerData) => {
    const userId = getUserId();
    try {
      const response = await api.post(
        `/Customer?userId=${userId}`,
        customerData,
      );
      return response.data;
    } catch (error) {
      // Re-throw with better error structure
      if (error.response?.data) {
        throw error;
      }
      throw new Error("Failed to add customer");
    }
  },

  deleteCustomer: async (customerId) => {
    const userId = getUserId();
    try {
      const response = await api.delete(
        `/Customer/${customerId}?userId=${userId}`,
      );
      return response.data;
    } catch (error) {
      if (error.response?.data) {
        throw error;
      }
      throw new Error("Failed to delete customer");
    }
  },

  updateCustomer: async (customerData) => {
    const userId = getUserId();
    try {
      const response = await api.put(
        `/Customer/${customerData.customerId}?userId=${userId}`,
        customerData,
      );
      return response.data;
    } catch (error) {
      if (error.response?.data) {
        throw error;
      }
      throw new Error("Failed to update customer");
    }
  },
};

// Daily Milk Entry API
export const dailyMilkAPI = {
  saveDailyEntries: async (entryDate, entries, isHoliday = false) => {
    const userId = getUserId();
    const dateStr =
      entryDate instanceof Date
        ? entryDate.toISOString().split("T")[0]
        : entryDate;
    const response = await api.post(`/DailyMilkEntry?userId=${userId}`, {
      entryDate: dateStr,
      entries,
      isHoliday, // Add holiday flag
    });
    return response.data;
  },

  getDailyEntries: async (entryDate) => {
    const userId = getUserId();
    const dateStr =
      entryDate instanceof Date
        ? entryDate.toISOString().split("T")[0]
        : entryDate;
    const response = await api.get(
      `/DailyMilkEntry?entryDate=${dateStr}&userId=${userId}`,
    );
    return response.data;
  },
};

// Milk Rate API
export const milkRateAPI = {
  getMilkRates: async () => {
    const userId = getUserId();
    const response = await api.get(`/MilkRate/${userId}`);
    return response.data;
  },

  updateMilkRates: async (cowRate, buffaloRate) => {
    const userId = getUserId();
    const response = await api.put(`/MilkRate?userId=${userId}`, {
      cowRate,
      buffaloRate,
    });
    return response.data;
  },
};

// Dairy Info API
export const dairyInfoAPI = {
  getDairyInfo: async () => {
    const userId = getUserId();
    const response = await api.get(`/DairyInfo/${userId}`);
    return response.data;
  },

  saveDairyInfo: async (formData) => {
    const userId = getUserId();
    // ✅ FIX: Remove Content-Type header for FormData - let browser set it with boundary
    const response = await api.post(`/DairyInfo?userId=${userId}`, formData, {
      headers: {
        "Content-Type": undefined, // This removes the default JSON header
      },
    });
    return response.data;
  },
};

// Purchase Milk API
export const purchaseMilkAPI = {
  savePurchaseEntry: async (purchaseData) => {
    const userId = getUserId();
    const response = await api.post(
      `/PurchaseMilk?userId=${userId}`,
      purchaseData,
    );
    return response.data;
  },

  updatePurchaseEntry: async (purchaseData) => {
    const userId = getUserId();
    const response = await api.post(
      `/PurchaseMilk?userId=${userId}`,
      purchaseData,
    );
    return response.data;
  },

  deletePurchaseEntry: async (purchaseEntryId) => {
    const userId = getUserId();
    const response = await api.delete(
      `/PurchaseMilk/${purchaseEntryId}?userId=${userId}`,
    );
    return response.data;
  },

  getLast5DaysEntries: async () => {
    const userId = getUserId();
    const response = await api.get(
      `/PurchaseMilk/GetlastFivedaysEntris?userId=${userId}`,
    );
    return response.data;
  },
};

// Other Expense API
export const otherExpenseAPI = {
  saveExpense: async (expenseData) => {
    const userId = getUserId();
    const response = await api.post(
      `/OtherExpense?userId=${userId}`,
      expenseData,
    );
    return response.data;
  },

  getExpenses: async (startDate = null, endDate = null) => {
    const userId = getUserId();
    let url = `/OtherExpense?userId=${userId}`;
    if (startDate) url += `&startDate=${startDate}`;
    if (endDate) url += `&endDate=${endDate}`;
    const response = await api.get(url);
    return response.data;
  },

  deleteExpense: async (expenseId) => {
    const userId = getUserId();
    const response = await api.delete(
      `/OtherExpense/${expenseId}?userId=${userId}`,
    );
    return response.data;
  },
};

// Sales API
export const salesAPI = {
  saveSalesEntry: async (salesData) => {
    const userId = getUserId();
    const response = await api.post(`/Sales?userId=${userId}`, salesData);
    return response.data;
  },

  getSalesEntries: async (startDate = null, endDate = null) => {
    const userId = getUserId();
    let url = `/Sales?userId=${userId}`;
    if (startDate) url += `&startDate=${startDate}`;
    if (endDate) url += `&endDate=${endDate}`;
    const response = await api.get(url);
    return response.data;
  },

  deleteSalesEntry: async (salesId) => {
    const userId = getUserId();
    const response = await api.delete(`/Sales/${salesId}?userId=${userId}`);
    return response.data;
  },
};

// Dashboard API
export const dashboardAPI = {
  getDashboardSummary: async (
    period,
    year,
    month,
    quarter,
    startDate,
    endDate,
  ) => {
    const userId = getUserId();
    const response = await api.post(`/Dashboard?userId=${userId}`, {
      period,
      year,
      month,
      quarter,
      startDate,
      endDate,
    });
    return response.data;
  },

  getMonthlySoldMilk: async () => {
    const userId = getUserId();
    const response = await api.get(
      `/Dashboard/monthlysoldmilk?userId=${userId}`,
    );
    return response.data;
  },

  getDashboardData: async (request) => {
    const userId = getUserId();
    const response = await api.post(
      `/Dashboard/data?userId=${userId}`,
      request,
    );
    return response.data;
  },
};

// Report API
// ✅ OPTIMIZED Report API with proper timeout handling
export const reportAPI = {
  getCustomerBills: async (
    period,
    year,
    month,
    quarter,
    startDate,
    endDate,
  ) => {
    const userId = getUserId();
    const response = await api.post(`/Report/customer-bills?userId=${userId}`, {
      period,
      year,
      month,
      quarter,
      startDate,
      endDate,
    });
    return response.data;
  },

  sendMonthlyBill: async (
    customerId,
    language = "english",
    year = null,
    month = null,
  ) => {
    const userId = getUserId();
    let url = `/Report/send-monthly-bill/${customerId}?userId=${userId}&language=${language}`;
    if (year !== null && month !== null) {
      url += `&year=${year}&month=${month}`;
    }
    const response = await api.post(url, {}, { timeout: 60000 });
    return response.data;
  },

  downloadBill: async (customerId) => {
    const userId = getUserId();
    const response = await api.get(
      `/Report/download-bill/${customerId}?userId=${userId}`,
      { responseType: "blob" },
    );

    const url = window.URL.createObjectURL(new Blob([response]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `Bill_${customerId}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();

    return response;
  },

  /**
   * Get customer's daily milk entries for a specific month
   * GET api/dailymilkentry/monthly?customerId=X&userId=Y&year=Z&month=M
   * @param {number} customerId - Customer ID
   * @param {number} year - Year (e.g., 2026)
   * @param {number} month - Month (1-12)
   * @returns {Promise} Response with array of MonthlyMilkEntryDTO
   */
  getCustomerMonthlyEntries: async (customerId, year, month) => {
    const userId = getUserId();
    const response = await api.get(
      `/DailyMilkEntry/monthly?customerId=${customerId}&userId=${userId}&year=${year}&month=${month}`,
    );
    return response.data;
  },

  /**
   * Update a customer's daily milk entry
   * PUT api/dailymilkentry/{entryId}?userId=Y
   * Body: { QuantityLiters, MilkRate }
   * @param {number} entryId - Entry ID from MonthlyMilkEntryDTO.EntryId
   * @param {number} quantityLiters - Milk quantity in liters
   * @param {number} milkRate - Milk rate per liter
   * @returns {Promise} Response with success status
   */
  updateCustomerDailyEntry: async (entryId, quantityLiters, milkRate) => {
    const userId = getUserId();
    const response = await api.put(
      `/DailyMilkEntry/${entryId}?userId=${userId}`,
      { QuantityLiters: quantityLiters, MilkRate: milkRate },
    );
    return response.data;
  },
};

// Billing API
export const billingAPI = {
  saveBillPayment: async (paymentData) => {
    const userId = getUserId();
    const response = await api.post(
      `/Billing/payment?userId=${userId}`,
      paymentData,
    );
    return response.data;
  },

  /**
   * Get customer's billing history
   * GET api/billing/bill-history?customerId=X&userId=Y&startDate=YYYY-MM-DD&endDate=YYYY-MM-DD
   * @param {number} customerId - Customer ID
   * @param {string} startDate - Optional start date (YYYY-MM-DD)
   * @param {string} endDate - Optional end date (YYYY-MM-DD)
   * @returns {Promise} Response with array of BillHistoryDTO
   */
  getBillHistory: async (customerId, startDate = null, endDate = null) => {
    const userId = getUserId();
    let url = `/Billing/bill-history?customerId=${customerId}&userId=${userId}`;
    if (startDate) url += `&startDate=${startDate}`;
    if (endDate) url += `&endDate=${endDate}`;
    const response = await api.get(url);
    return response.data;
  },

  /**
   * Get last 4 months bill totals for a customer
   * GET api/billing/4month-bill-totals?customerId=X&userId=Y
   * @param {number} customerId - Customer ID
   * @returns {Promise} Response with array of { year, month, currentBill }
   */
  getCustomer4MonthBillTotals: async (customerId) => {
    const userId = getUserId();
    const response = await api.get(
      `/Billing/4month-bill-totals?customerId=${customerId}&userId=${userId}`,
    );
    return response.data;
  },
};

// User Management API (Admin only)
export const userManagementAPI = {
  // Admin login
  adminLogin: async (userName, password) => {
    const response = await api.post("/Admin/login", {
      userName: userName,
      password: password,
    });
    return response.data;
  },

  // Get all users for admin panel
  getAllUsers: async () => {
    const response = await api.get("/Admin/users");
    return response.data;
  },

  // Toggle user active status
  toggleUserStatus: async (targetUserId, activeStatus) => {
    const response = await api.post(
      `/Admin/toggle-status?userId=${targetUserId}&activeStatus=${activeStatus}`,
    );
    return response.data;
  },

  // Reset user password
  resetPassword: async (targetUserId, newPassword) => {
    const response = await api.post("/Admin/reset-password", {
      userId: targetUserId,
      newPassword: newPassword,
    });
    return response.data;
  },

  // Set subscription
  setSubscription: async (targetUserId, totalMonths) => {
    const response = await api.put(`/Admin/set-subscription`, {
      userId: targetUserId,
      subscriptionMonths: totalMonths,
    });
    return response.data;
  },
};

// Allowance API
export const allowanceAPI = {
  getAllowances: async () => {
    const userId = getUserId();
    const response = await api.get(`/Allowance/get?userId=${userId}`);
    return response.data;
  },

  saveAllowances: async (allowanceData) => {
    const userId = getUserId();
    const response = await api.post(
      `/Allowance/save?userId=${userId}`,
      allowanceData,
    );
    return response.data;
  },
};

// Customer Leave API
export const customerLeaveAPI = {
  getLeaves: async () => {
    const userId = getUserId();
    const response = await api.get(`/CustomerLeave?userId=${userId}`);
    return response.data;
  },

  getLeavesByDate: async (date) => {
    const userId = getUserId();
    const response = await api.get(
      `/CustomerLeave/by-date?userId=${userId}&date=${date}`,
    );
    return response.data;
  },

  addLeave: async (customerId, startDate, endDate, reason) => {
    const userId = getUserId();
    const response = await api.post(`/CustomerLeave?userId=${userId}`, {
      customerId,
      startDate,
      endDate,
      reason,
    });
    return response.data;
  },

  updateLeave: async (leaveId, customerId, startDate, endDate, reason) => {
    const userId = getUserId();
    const response = await api.put(
      `/CustomerLeave/${leaveId}?userId=${userId}`,
      {
        customerId,
        startDate,
        endDate,
        reason,
      },
    );
    return response.data;
  },

  deleteLeave: async (leaveId) => {
    const userId = getUserId();
    const response = await api.delete(
      `/CustomerLeave/${leaveId}?userId=${userId}`,
    );
    return response.data;
  },
};

// Employee API
export const employeeAPI = {
  getEmployeesByUser: async (userId) => {
    const response = await api.get(`/Employee/by-user/${userId}`);
    return response.data;
  },

  createEmployee: async (userId, username, password) => {
    const response = await api.post(`/Employee?userId=${userId}`, {
      username,
      password,
    });
    return response.data;
  },

  updateEmployee: async (employeeId, userId, username, password) => {
    const response = await api.put(`/Employee/${employeeId}?userId=${userId}`, {
      employeeId,
      username,
      password,
    });
    return response.data;
  },

  deleteEmployee: async (employeeId) => {
    const response = await api.delete(`/Employee/${employeeId}`);
    return response.data;
  },

  toggleEmployeeStatus: async (employeeId, isActive) => {
    const response = await api.put(
      `/Employee/toggle-status/${employeeId}?isActive=${isActive}`,
    );
    return response.data;
  },
};

// Module Permission API
export const modulePermissionAPI = {
  getUserPermissions: async (userId) => {
    const response = await api.get(`/Permissions/user/${userId}`);
    return response.data;
  },

  setUserPermissions: async (userId, permissions) => {
    const response = await api.put(`/Permissions/user/${userId}`, {
      permissions,
    });
    return response.data;
  },

  getEmployeePermissions: async (employeeId) => {
    const response = await api.get(`/Permissions/employee/${employeeId}`);
    return response.data;
  },

  setEmployeePermissions: async (employeeId, permissions) => {
    const response = await api.put(`/Permissions/employee/${employeeId}`, {
      permissions,
    });
    return response.data;
  },

  getDefaultTemplate: async () => {
    const response = await api.get(`/Permissions/default-template`);
    return response.data;
  },

  setDefaultTemplate: async (permissions) => {
    const response = await api.put(`/Permissions/default-template`, {
      permissions,
    });
    return response.data;
  },

  /** Returns the current user's enabled module codes from the database. */
  getMyPermissions: async () => {
    const response = await api.get(`/Permissions/my-permissions`);
    return response.data;
  },
};

// Customer Assignment API
export const customerAssignmentAPI = {
  assignCustomer: async (employeeId, customerId) => {
    const response = await api.post(`/customer-assignment/assign`, {
      employeeId,
      customerId,
    });
    return response.data;
  },

  removeCustomer: async (customerId) => {
    const response = await api.post(`/customer-assignment/remove`, {
      customerId,
    });
    return response.data;
  },

  getEmployeeCustomers: async (employeeId) => {
    const response = await api.get(
      `/customer-assignment/by-employee/${employeeId}`,
    );
    return response.data;
  },

  getUnassignedCustomers: async (userId) => {
    const response = await api.get(`/customer-assignment/unassigned/${userId}`);
    return response.data;
  },

  getMyCustomers: async (employeeId) => {
    const response = await api.get(
      `/customer-assignment/my-customers?employeeId=${employeeId}`,
    );
    return response.data;
  },
};

export default api;
